import React from "react";

/**
 * Composer isolado com mapeamento de classes:
 * - Usa suas classes CSS via props (inputClassName, popupClassNames)
 * - Enter envia / Shift+Enter quebra / respostas rápidas com "/"
 * - Expoõe métodos via ref: send(), focus(), getValue(), setValue()
 */
const Composer = React.memo(React.forwardRef(function Composer(
  {
    onSend,
    disabled,
    fetchQuickReplies,
    inputClassName = "composer-input",
    popupClassNames = {
      container: "rr-popup",
      list: "rr-list",
      item: "rr-item",
      itemAtivo: "is-active",
      titulo: "rr-item-title",
      corpo: "rr-item-body",
    },
  },
  ref
) {
  const [texto, setTexto] = React.useState("");
  const [rrVisivel, setRrVisivel] = React.useState(false);
  const [rrLista, setRrLista] = React.useState([]);
  const [rrIndex, setRrIndex] = React.useState(0);
  const areaRef = React.useRef(null);

  // Detecta Ctrl+V com imagem
React.useEffect(() => {
  const handlePaste = (e) => {
    if (!e.clipboardData) return;
    const items = e.clipboardData.items;
    if (!items || items.length === 0) return;

    for (const item of items) {
      if (item.type.indexOf("image") !== -1) {
        const file = item.getAsFile();
        if (!file) return;
        // dispara evento customizado pro pai (PainelMensagens)
        const customEvent = new CustomEvent("composer-paste-image", { detail: file });
        window.dispatchEvent(customEvent);
        e.preventDefault();
        break;
      }
    }
  };

  const el = areaRef.current;
  if (el) el.addEventListener("paste", handlePaste);
  return () => el && el.removeEventListener("paste", handlePaste);
}, []);

  
  const debRef = React.useRef();
  const lastActionRef = React.useRef(null);


 const rafIdRef = React.useRef(0);
 const scheduleResize = React.useCallback(() => {
   if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
   rafIdRef.current = requestAnimationFrame(() => {
     const el = areaRef.current;
     if (!el) return;
     const max = 120;                  // igual ao seu style maxHeight
     el.style.height = 'auto';         // mede naturalmente
     const next = Math.min(el.scrollHeight, max);
     el.style.height = next + 'px';
     el.style.overflowY = el.scrollHeight > max ? 'auto' : 'hidden';
   });
 }, []);
 React.useEffect(() => { scheduleResize(); }, [texto, scheduleResize]);



  // === buscar respostas rápidas com debounce (igual ao seu)
  const triggerRR = React.useCallback((q) => {
    clearTimeout(debRef.current);
    debRef.current = setTimeout(async () => {
      try {
        if (!fetchQuickReplies) return;
        const data = await fetchQuickReplies(q || "");
        setRrLista(Array.isArray(data) ? data : []);
      } catch {
        setRrLista([]);
      }
    }, 180);
  }, [fetchQuickReplies]);

  // === enviar (reutilizado internamente e exposto ao pai)
const sendNow = React.useCallback(() => {
  const msg = texto.trim();
  if (!msg) return false;
  const toSend = texto;
  setTexto("");
  setRrVisivel(false);
  onSend?.(toSend);

  // marca que o último evento foi "send"
  lastActionRef.current = "sent";

  // tenta focar já no próximo frame
  requestAnimationFrame(() => {
  scheduleResize();
  areaRef.current?.focus();
  });

  return true;
}, [texto, onSend]);


  // === API imperativa para o pai (botão "send" chamar)
  React.useImperativeHandle(ref, () => ({
    send: () => sendNow(),
    focus: () => { areaRef.current?.focus(); },
    getValue: () => texto,
    setValue: (v) => setTexto(v ?? ""),
  }), [sendNow, texto]);

  React.useEffect(() => {
  if (!disabled && lastActionRef.current === "sent") {
    // dá mais uma chance de focar após o re-render do pai
    requestAnimationFrame(() => {
      scheduleResize();
      areaRef.current?.focus();
      // fallback extra para casos mais lentos
      setTimeout(() => areaRef.current?.focus(), 0);
      lastActionRef.current = null;
    });
  }
}, [disabled]);


  // === onChange (mesma lógica do seu textarea)
  const handleChange = (e) => {
    const txt = e.target.value;
    setTexto(txt);
    scheduleResize();

    const cursor = e.target.selectionStart ?? txt.length;
    const ateCursor = txt.slice(0, cursor);
    const match = ateCursor.match(/\/([\w-]*)$/);
    if (match) {
      triggerRR(match[1] || "");
      setRrVisivel(true);
      setRrIndex(0);
    } else {
      setRrVisivel(false);
    }
  };

  // === inserir item selecionado no local do "/termo"
  const aplicarRespostaRapida = (item) => {
    if (!item) return;
    setTexto((prev) => {
      const el = areaRef.current;
      const pos = el ? (el.selectionStart ?? prev.length) : prev.length;
      const antes = prev.slice(0, pos).replace(/\/[\w-]*$/, item.conteudo);
      const depois = prev.slice(pos);
      const novo = `${antes}${depois}`;
      requestAnimationFrame(() => {
        if (areaRef.current) {
          const newPos = antes.length;
          areaRef.current.selectionStart = areaRef.current.selectionEnd = newPos;
          scheduleResize();
          areaRef.current.focus();
        }
      });
      return novo;
    });
    setRrVisivel(false);
  };

  // === teclas (igual ao seu comportamento)
  const handleKeyDown = (e) => {
    if (rrVisivel) {
      if (e.key === "ArrowDown") { e.preventDefault(); setRrIndex((i) => Math.min(i + 1, Math.max(0, rrLista.length - 1))); return; }
      if (e.key === "ArrowUp")   { e.preventDefault(); setRrIndex((i) => Math.max(i - 1, 0)); return; }
      if (e.key === "Escape")    { setRrVisivel(false); return; }
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        aplicarRespostaRapida(rrLista[rrIndex]);
        return;
      }
    } else if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendNow();  // usa a função local; o pai pode chamar via ref também
    }
  };

  return (
    <div style={{ position: "relative", width: "100%", marginTop: "10px" }}>
      <textarea
        ref={areaRef}
        className={inputClassName}
        placeholder='Digite sua mensagem (use "/" para respostas rápidas)'
        value={texto}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onInput={scheduleResize} 
        rows={1}
        disabled={disabled}
        style={{ width: "100%", resize: "none", minHeight: 28, maxHeight: 120, overflowY: "hidden", willChange: "height"  }}

      />

      {rrVisivel && rrLista.length > 0 && (
        <div className={popupClassNames.container}>
          <div className={popupClassNames.list}>
            {rrLista.map((item, idx) => {
              const slugify = (s) => (s || "")
                .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
                .toLowerCase().replace(/[^\w]+/g, "");
              const atalho = item.slug || item.atalho || item.comando || slugify(item.titulo || item.conteudo);
              const ativo = idx === rrIndex ? popupClassNames.itemAtivo : "";

              return (
                <button
                  key={item.id ?? idx}
                  type="button"
                  className={`${popupClassNames.item} ${ativo}`}
                  onMouseDown={(e) => { e.preventDefault(); aplicarRespostaRapida(item); }}
                  onMouseEnter={() => setRrIndex(idx)}
                  title={item.titulo || ""}
                >
                  <div className={popupClassNames.titulo}>
                    <span className="rr-item-slash">{atalho}</span>
                    <span className="rr-item-dash"> — </span>
                    <span className="rr-item-strong">{item.titulo || "Sem título"}</span>
                  </div>
                  <div className={popupClassNames.corpo}>
                    {item.conteudo?.length > 140 ? item.conteudo.slice(0, 140) + "…" : item.conteudo}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}));

export default Composer;
